import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, Search, Send, Users, Pencil, Trash2, Plus } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const CONFIRM_THRESHOLD = 25;

const TEMPLATES = [
  { key: "", label: "Start from scratch", subject: "", body: "" },
  { key: "ride-cancelled", label: "Ride / session cancelled",
    subject: "This weekend's ride is cancelled",
    body: "Hi everyone,\n\nUnfortunately we've had to cancel this weekend's ride due to the conditions. We'll be in touch with a new date shortly.\n\nApologies for any inconvenience, and stay safe." },
  { key: "renewal-reminder", label: "Membership renewal reminder",
    subject: "Time to renew your club membership",
    body: "Hi,\n\nThis is a friendly reminder that your annual club membership is due for renewal. You can renew in a couple of taps from your Member Area using the 'Renew membership' button.\n\nThank you for your continued support." },
  { key: "event-reminder", label: "Upcoming event reminder",
    subject: "Reminder: upcoming club event",
    body: "Hi everyone,\n\nJust a reminder about our upcoming event. Please check the Events page for the time and meeting point, and let us know if your plans change.\n\nLooking forward to seeing you there." },
  { key: "welcome", label: "General notice",
    subject: "A quick note from RoSPA West Midlands",
    body: "Hi,\n\n[Write your update here.]\n\nBest wishes,\nThe committee" },
];

const fmtSize = (b) => {
  if (!b) return "";
  const u = ["B", "KB", "MB", "GB"]; let i = 0, n = b;
  while (n >= 1024 && i < 3) { n /= 1024; i++; }
  return `${n.toFixed(i > 0 && n < 10 ? 1 : 0)} ${u[i]}`;
};
const fileType = (d) => {
  const ct = (d.content_type || "").toLowerCase();
  const fn = (d.original_filename || "").toLowerCase();
  if (ct.includes("pdf") || fn.endsWith(".pdf")) return "PDF";
  if (ct.includes("word") || /\.docx?$/.test(fn)) return "Word document";
  if (ct.includes("sheet") || /\.xlsx?$/.test(fn)) return "Spreadsheet";
  if (ct.startsWith("image/")) return "Image";
  const ext = fn.split(".").pop();
  return ext && ext.length <= 4 ? ext.toUpperCase() : "File";
};

function TemplatesDialog({ open, onOpenChange, templates, setTemplates }) {
  const empty = { label: "", subject: "", body: "" };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (t) => { setEditing(t.id); setForm({ label: t.label, subject: t.subject || "", body: t.body || "" }); };

  const save = async () => {
    if (!form.label.trim()) { toast.error("Give the template a name"); return; }
    setBusy(true);
    try {
      if (editing === "new") {
        const { data } = await api.post("/messaging/templates", form);
        setTemplates([...templates, data]);
        toast.success("Template saved");
      } else {
        await api.patch(`/messaging/templates/${editing}`, form);
        setTemplates(templates.map((t) => (t.id === editing ? { ...t, ...form } : t)));
        toast.success("Template updated");
      }
      setEditing(null); setForm(empty);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the template");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/messaging/templates/${id}`);
      setTemplates(templates.filter((t) => t.id !== id));
      if (editing === id) { setEditing(null); setForm(empty); }
      toast.success("Template deleted");
    } catch { toast.error("Couldn't delete the template"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="templates-dialog">
        <DialogHeader><DialogTitle>Manage club templates</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {templates.length === 0 && <p className="text-sm text-slate-400">No saved templates yet. Add one below.</p>}
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`template-row-${t.id}`}>
              <span className="text-sm font-medium text-[#0F2744] truncate">{t.label}</span>
              <span className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => startEdit(t)} data-testid={`template-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(t.id)} className="text-red-600" data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
              </span>
            </div>
          ))}
        </div>
        {editing ? (
          <div className="space-y-2 border-t border-slate-100 pt-3">
            <Input placeholder="Template name" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="template-form-label" />
            <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="template-form-subject" />
            <Textarea rows={5} placeholder="Message" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="template-form-body" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setEditing(null); setForm(empty); }}>Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-[#0F2744] text-white" data-testid="template-form-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save template"}</Button>
            </div>
          </div>
        ) : (
          <Button onClick={startNew} variant="outline" className="w-full" data-testid="template-add-btn"><Plus className="h-4 w-4 mr-1.5" /> New template</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
};

export default function Messaging() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [aud, setAud] = useState(null);           // audiences payload
  const [audience, setAudience] = useState("");   // selected audience key
  const [group, setGroup] = useState("");
  const [eventId, setEventId] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [templateKey, setTemplateKey] = useState("");
  const [attachDocId, setAttachDocId] = useState("");
  const [customTemplates, setCustomTemplates] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/messaging/audiences")
      .then(({ data }) => {
        if (!active) return;
        setAud(data);
        setCustomTemplates(data.templates || []);
        setAudience(data.scope === "associates" ? "my_associates" : "all");
      })
      .catch(() => toast.error("You don't have permission to send messages."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const members = aud?.members || [];
  const full = aud?.scope === "all";

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q));
  }, [members, search]);

  const recipientCount = useMemo(() => {
    if (audience === "individuals") return selected.size;
    if (audience === "all") return members.length;
    if (audience === "my_associates") return members.length;
    if (audience === "group") return aud?.groups?.find((g) => g.key === group)?.count ?? 0;
    if (audience === "event") return aud?.events?.find((e) => e.id === eventId)?.count ?? 0;
    return 0;
  }, [audience, selected, members, aud, group, eventId, full]);

  const audienceLabel = useMemo(() => {
    if (audience === "all") return "whole club";
    if (audience === "group") return (aud?.groups?.find((g) => g.key === group)?.label || "").toLowerCase();
    if (audience === "event") return aud?.events?.find((e) => e.id === eventId)?.title || "the event";
    if (audience === "individuals") return "selected people";
    return "";
  }, [audience, aud, group, eventId]);

  const canSend = subject.trim() && body.trim() && recipientCount > 0 && !sending
    && (audience !== "group" || group)
    && (audience !== "event" || eventId)
    && (audience !== "individuals" || selected.size > 0);

  const requestSend = () => {
    if (!canSend) return;
    if (recipientCount >= CONFIRM_THRESHOLD) { setConfirmOpen(true); return; }
    doSend();
  };

  const doSend = async () => {
    if (!canSend) return;
    setConfirmOpen(false);
    setSending(true);
    setResult(null);
    try {
      const payload = { audience, subject: subject.trim(), body: body.trim() };
      if (audience === "group") payload.group = group;
      if (audience === "event") payload.event_id = eventId;
      if (audience === "individuals") payload.member_ids = [...selected];
      if (attachDocId) payload.document_id = attachDocId;
      const { data } = await api.post("/messaging/send", payload);
      setResult(data);
      if (data.failed) toast.warning(`Sent to ${data.sent}, ${data.failed} could not be delivered`);
      else toast.success(`Sent to ${data.sent} ${data.sent === 1 ? "person" : "people"}`);
      setSubject(""); setBody(""); setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const AudienceOption = ({ value, title, desc }) => (
    <button
      type="button"
      onClick={() => setAudience(value)}
      data-testid={`audience-${value}`}
      className={`text-left rounded-xl border-2 p-4 transition-colors ${audience === value ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
    >
      <div className="font-bold text-[#0F2744]">{title}</div>
      <div className="text-sm text-slate-500 mt-0.5">{desc}</div>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="messaging-page">
      <header className="bg-[#0F2744] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
          <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 px-2">
            <Link to={user?.is_owner || ["admin", "committee"].includes(user?.role) ? "/admin" : "/member"} data-testid="messaging-back-btn">
              <ArrowLeft className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Back</span>
            </Link>
          </Button>
          <div className="flex items-center gap-2 text-white font-bold"><Mail className="h-5 w-5 text-amber-400" /> Messaging</div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-extrabold text-[#0F2744]">Email members</h1>
        <p className="text-slate-500 mt-1">
          Send an email to an individual, a group, or the whole club. Each person receives it privately and replies come
          straight back to you.
        </p>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0F2744]" /></div>
        ) : !aud ? (
          <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-8 text-center text-slate-500">
            You don't have permission to send messages.
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {/* Step 1: audience */}
            <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
              <h2 className="font-bold text-[#0F2744] mb-4">1 · Who should receive this?</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {full ? (
                  <>
                    <AudienceOption value="all" title="Whole club" desc={`Everyone with an account (${members.length})`} />
                    <AudienceOption value="group" title="A role or status group" desc="e.g. all Members, Committee, Tutors, Lapsed" />
                    <AudienceOption value="individuals" title="Specific people" desc="Search and tick individual members" />
                    <AudienceOption value="event" title="People booked on an event" desc="Everyone confirmed for a chosen event" />
                  </>
                ) : (
                  <>
                    <AudienceOption value="my_associates" title="My assigned associates" desc={`The members assigned to you (${members.length})`} />
                    <AudienceOption value="individuals" title="Specific associates" desc="Pick individual associates" />
                  </>
                )}
              </div>

              {audience === "group" && (
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {aud.groups.map((g) => (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => setGroup(g.key)}
                      data-testid={`group-${g.key}`}
                      className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${group === g.key ? "border-amber-500 bg-amber-50 font-semibold" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <span className="text-[#0F2744]">{g.label}</span>
                      <span className="text-slate-400">{g.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {audience === "event" && (
                <div className="mt-4">
                  <select
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    data-testid="messaging-event-select"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#0F2744] bg-white"
                  >
                    <option value="">Choose an event…</option>
                    {aud.events.map((e) => (
                      <option key={e.id} value={e.id}>{e.title} · {fmtDate(e.date)} ({e.count} booked)</option>
                    ))}
                  </select>
                  {aud.events.length === 0 && <p className="text-sm text-slate-400 mt-2">No events yet.</p>}
                </div>
              )}

              {audience === "individuals" && (
                <div className="mt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by name or email"
                      className="pl-9"
                      data-testid="messaging-search"
                    />
                  </div>
                  <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {filteredMembers.length === 0 ? (
                      <p className="p-4 text-sm text-slate-400">No people match that search.</p>
                    ) : filteredMembers.map((m) => (
                      <label key={m.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer" data-testid={`recipient-${m.id}`}>
                        <input
                          type="checkbox"
                          checked={selected.has(m.id)}
                          onChange={() => toggle(m.id)}
                          className="h-4 w-4 accent-amber-500"
                          data-testid={`recipient-check-${m.id}`}
                        />
                        <span className="flex-1">
                          <span className="font-medium text-[#0F2744]">{m.name}</span>
                          <span className="text-slate-400 text-sm"> · {m.email}</span>
                        </span>
                        <span className="text-xs uppercase tracking-wide text-slate-400">{m.role}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Step 2: message */}
            <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
              <h2 className="font-bold text-[#0F2744] mb-4">2 · Your message</h2>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-slate-600">Start from a template (optional)</label>
                {aud.can_manage_templates && (
                  <button type="button" onClick={() => setManageOpen(true)} className="text-xs font-semibold text-amber-600 hover:underline" data-testid="manage-templates-btn">Manage templates</button>
                )}
              </div>
              <select
                value={templateKey}
                onChange={(e) => {
                  const v = e.target.value;
                  setTemplateKey(v);
                  const tpl = v.startsWith("custom:")
                    ? customTemplates.find((x) => `custom:${x.id}` === v)
                    : TEMPLATES.find((x) => x.key === v);
                  if (tpl) { setSubject(tpl.subject || ""); setBody(tpl.body || ""); }
                }}
                data-testid="messaging-template-select"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#0F2744] bg-white mb-4">
                {TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                {customTemplates.length > 0 && (
                  <optgroup label="Your club templates">
                    {customTemplates.map((t) => <option key={t.id} value={`custom:${t.id}`}>{t.label}</option>)}
                  </optgroup>
                )}
              </select>
              <label className="block text-sm font-medium text-slate-600 mb-1">Subject</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Saturday ride — meeting point change" data-testid="messaging-subject" />
              <label className="block text-sm font-medium text-slate-600 mb-1 mt-4">Message</label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder="Write your message here…" data-testid="messaging-body" />
              <p className="text-xs text-slate-400 mt-2">Replies go to {aud.sender_email}. The club signature is added automatically.</p>
              {aud.documents?.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-600 mb-1">Attach a club document (optional)</label>
                  <select
                    value={attachDocId}
                    onChange={(e) => setAttachDocId(e.target.value)}
                    data-testid="messaging-attach-select"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#0F2744] bg-white">
                    <option value="">No attachment</option>
                    {aud.documents.map((d) => (
                      <option key={d.id} value={d.id}>{d.title}{d.category ? ` · ${d.category}` : ""}</option>
                    ))}
                  </select>
                  {attachDocId && (() => {
                    const d = aud.documents.find((x) => x.id === attachDocId);
                    if (!d) return null;
                    const parts = [fileType(d), fmtSize(d.size)].filter(Boolean);
                    return <p className="text-xs text-slate-500 mt-1 font-medium" data-testid="attach-preview">{parts.join(" · ") || "External link"}{d.original_filename ? ` · ${d.original_filename}` : ""}</p>;
                  })()}
                  <p className="text-xs text-slate-400 mt-1">A link to this document is added to the email; members open it from their Member Area.</p>
                </div>
              )}
            </section>

            {/* Delivery summary */}
            {result && (
              <div
                data-testid="messaging-result"
                className={`rounded-2xl border p-4 flex items-start gap-3 ${result.failed ? "border-amber-300 bg-amber-50" : "border-green-300 bg-green-50"}`}>
                <div className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center text-white text-xs font-bold ${result.failed ? "bg-amber-500" : "bg-green-600"}`}>
                  {result.failed ? "!" : "✓"}
                </div>
                <div className="text-sm">
                  <p className="font-bold text-[#0F2744]">
                    Delivered to {result.sent} of {result.total} {result.total === 1 ? "recipient" : "recipients"}
                  </p>
                  <p className="text-slate-600 mt-0.5">
                    Sent to {result.audience_label}.
                    {result.failed
                      ? ` ${result.failed} could not be delivered (an address may be invalid or unreachable).`
                      : " Everyone received it."}
                  </p>
                  {result.failed > 0 && result.failed_recipients?.length > 0 && (
                    <div className="mt-2" data-testid="messaging-failed-list">
                      <p className="font-semibold text-amber-800">Could not be delivered — please follow up:</p>
                      <ul className="list-disc pl-5 text-amber-800">
                        {result.failed_recipients.map((r, i) => (
                          <li key={i}>{r.name} ({r.email})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Send bar */}
            <div className="sticky bottom-4">
              <div className="rounded-2xl bg-[#0F2744] text-white p-4 flex items-center justify-between gap-4 shadow-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-5 w-5 text-amber-400" />
                  <span data-testid="messaging-recipient-count">
                    {recipientCount} {recipientCount === 1 ? "recipient" : "recipients"} selected
                  </span>
                </div>
                <Button onClick={requestSend} disabled={!canSend} className="bg-amber-500 hover:bg-amber-400 text-[#0F2744] font-bold" data-testid="messaging-send-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send email</>}
                </Button>
              </div>
            </div>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogContent data-testid="messaging-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Send to {recipientCount} people?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This email will go to <strong>{recipientCount}</strong> recipients
                    {audienceLabel ? <> ({audienceLabel})</> : null}. Each person receives it privately. Please check your
                    subject and message before sending — this can't be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="messaging-confirm-cancel">Go back</AlertDialogCancel>
                  <AlertDialogAction onClick={doSend} className="bg-amber-500 hover:bg-amber-400 text-[#0F2744] font-bold" data-testid="messaging-confirm-send">
                    Yes, send to {recipientCount}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <TemplatesDialog open={manageOpen} onOpenChange={setManageOpen} templates={customTemplates} setTemplates={setCustomTemplates} />
          </div>
        )}
      </div>
    </div>
  );
}
